import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, CalendarX2, ChevronLeft, ChevronRight, List, Loader2, Plus, UserCheck } from 'lucide-react';
import { bookSlot, cancelLesson, fetchLessons, fetchProfile, fetchSlots } from '../api/candidate';
import { useAuthStore } from '../store/auth';
import { CandidateLesson, CandidateSlot } from '../types';
import { CAL_END_HOUR, CAL_START_HOUR, durationLabel, gridPosition, monitorInitials, slotLabel } from '../lib/format';
import dayjs from '../lib/dates';
import ReserveModal from '../components/ReserveModal';
import { Photo } from '../components/Photos';
import { Badge, Button, Card, ConfirmDialog, EmptyState, Segmented, cx } from '../components/charte';

// Réserver une leçon (refonte du 14/10/2026) : mêmes créneaux, mêmes appels. Liste par jour (seule vue sur
// téléphone, plus lisible qu'une grille de 7 colonnes) ; calendrier de la semaine à partir de la tablette.

type View = 'liste' | 'calendrier';

function Avatar({ name, photo }: { name: string | null; photo?: string | null }) {
  return (
    <span className="relative grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full bg-avatar text-xs font-semibold text-primary-700">
      {monitorInitials(name)}
      <Photo url={photo} />
    </span>
  );
}

/** Heure de début en grand, heure de fin dessous : repère visuel de la ligne. */
function TimeBlock({ startsAt, endsAt, tone = 'default' }: { startsAt: string; endsAt: string; tone?: 'default' | 'mine' }) {
  return (
    <span
      className={cx(
        'grid w-[62px] shrink-0 place-items-center rounded-2xl py-2 text-center leading-tight',
        tone === 'mine' ? 'bg-panel-indigo text-white' : 'bg-surface-indigo text-primary-700',
      )}
    >
      <span className="text-[15px] font-semibold">{dayjs(startsAt).format('HH:mm')}</span>
      <span className={cx('text-[11px]', tone === 'mine' ? 'text-white/75' : 'text-secondary')}>{dayjs(endsAt).format('HH:mm')}</span>
    </span>
  );
}

function dayTitle(d: dayjs.Dayjs) {
  const diff = d.startOf('day').diff(dayjs().startOf('day'), 'day');
  const label = d.format('dddd D MMMM');
  const cap = label.charAt(0).toUpperCase() + label.slice(1);
  return diff === 0 ? `Aujourd’hui · ${cap}` : diff === 1 ? `Demain · ${cap}` : cap;
}

export default function ReserveView() {
  const candidate = useAuthStore((s) => s.candidate);
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<CandidateSlot | null>(null);
  const [toCancel, setToCancel] = useState<CandidateLesson | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [view, setView] = useState<View>('liste');
  const [weekOffset, setWeekOffset] = useState(0);

  const slotsQuery = useQuery({ queryKey: ['candidate', 'slots'], queryFn: fetchSlots });
  const lessonsQuery = useQuery({ queryKey: ['candidate', 'lessons'], queryFn: fetchLessons });
  // Profil relu : la limite au moniteur référent peut avoir changé depuis la connexion.
  const profileQuery = useQuery({ queryKey: ['candidate', 'profile'], queryFn: fetchProfile });
  const referent = profileQuery.isSuccess
    ? (profileQuery.data?.access?.bookingReferent ?? null)
    : (candidate?.access?.bookingReferent ?? null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['candidate', 'slots'] });
    void queryClient.invalidateQueries({ queryKey: ['candidate', 'lessons'] });
  };

  const bookMutation = useMutation({
    mutationFn: (slotId: string) => bookSlot(slotId),
    onSuccess: () => {
      invalidate();
      setSelected(null);
      setActionError(null);
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const cancelMutation = useMutation({
    mutationFn: (lessonId: string) => cancelLesson(lessonId),
    onSuccess: () => {
      invalidate();
      setToCancel(null);
    },
    onError: (err: Error) => {
      setToCancel(null);
      setActionError(err.message);
    },
  });

  // ── Semaine courante (lundi → dimanche) ─────────────────────────────────
  const weekStart = useMemo(() => dayjs().startOf('week').add(weekOffset, 'week'), [weekOffset]);
  const weekEnd = weekStart.add(7, 'day');
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day')), [weekStart]);
  const inWeek = (iso: string) => {
    const d = dayjs(iso);
    return !d.isBefore(weekStart) && d.isBefore(weekEnd);
  };

  const allSlots = slotsQuery.data ?? [];
  const bookedLessons = (lessonsQuery.data ?? []).filter((l) => l.bookedOnline);
  const weekSlots = allSlots.filter((s) => inWeek(s.startsAt));
  const weekBooked = bookedLessons.filter((l) => inWeek(l.startsAt));
  const loading = slotsQuery.isLoading || lessonsQuery.isLoading;
  const slotsByDay = days
    .map((d) => ({ day: d, slots: weekSlots.filter((s) => dayjs(s.startsAt).isSame(d, 'day')) }))
    .filter((g) => g.slots.length > 0);

  const openModal = (slot: CandidateSlot) => {
    setActionError(null);
    setSelected(slot);
  };

  const hours = Array.from({ length: CAL_END_HOUR - CAL_START_HOUR }, (_, i) => CAL_START_HOUR + i);

  const list = (
    <div className="grid gap-6">
      {weekBooked.length > 0 && (
        <section className="grid gap-3">
          <h2 className="text-title-sm text-heading">Vos leçons réservées</h2>
          <div className="grid gap-3">
            {weekBooked.map((lesson) => {
              const isPast = new Date(lesson.endsAt).getTime() < Date.now();
              return (
                <Card key={lesson.id} className="flex items-center gap-3 !p-3.5 sm:!p-4">
                  <TimeBlock startsAt={lesson.startsAt} endsAt={lesson.endsAt} tone="mine" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-heading">{dayTitle(dayjs(lesson.startsAt))}</p>
                    <p className="truncate text-xs text-secondary">
                      {lesson.instructorName ?? 'Moniteur à confirmer'} · {durationLabel(lesson.startsAt, lesson.endsAt)} · Votre leçon
                    </p>
                  </div>
                  {isPast ? (
                    // Leçon passée : statut de présence (émargement) au lieu d'annuler.
                    lesson.honored ? <Badge tone="ok">Présent(e)</Badge> : <Badge tone="danger">Non honorée</Badge>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => {
                        setActionError(null);
                        setToCancel(lesson);
                      }}
                    >
                      Annuler
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>
        </section>
      )}

      <section className="grid gap-3">
        {weekBooked.length > 0 && <h2 className="text-title-sm text-heading">Créneaux disponibles</h2>}
        {weekSlots.length === 0 ? (
          <Card>
            <EmptyState
              icon={CalendarX2}
              title="Aucun créneau cette semaine"
              description="Naviguez vers une autre semaine ou revenez plus tard : votre auto-école ouvre régulièrement de nouveaux créneaux."
            />
          </Card>
        ) : (
          slotsByDay.map(({ day, slots }) => (
            <div key={day.toString()} className="grid gap-2">
              <h3 className="px-1 text-eyebrow uppercase text-muted">{dayTitle(day)}</h3>
              <div className="grid gap-2.5">
                {slots.map((slot) => (
                  <Card key={slot.id} interactive className="flex items-center gap-3 !p-3.5 sm:!p-4">
                    <TimeBlock startsAt={slot.startsAt} endsAt={slot.endsAt} />
                    <div className="flex min-w-0 flex-1 items-center gap-2.5">
                      <Avatar name={slot.instructorName} photo={slot.instructorPhotoUrl} />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-heading">{slot.instructorName ?? 'Moniteur à confirmer'}</p>
                        <p className="text-xs text-secondary">{durationLabel(slot.startsAt, slot.endsAt)} de conduite</p>
                      </div>
                    </div>
                    <Button size="sm" variant="soft" onClick={() => openModal(slot)}>
                      Réserver
                    </Button>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );

  const calendar = (
    <Card flush className="overflow-hidden">
      <div className="overflow-x-auto">
        <div className="min-w-[720px]">
          {/* En-tête des jours */}
          <div className="grid border-b border-line bg-surface-light" style={{ gridTemplateColumns: '56px repeat(7, minmax(0,1fr))' }}>
            <div />
            {days.map((d) => {
              const today = d.isSame(dayjs(), 'day');
              return (
                <div key={d.toString()} className="border-l border-slate-100 px-2 py-3 text-center">
                  <div className="text-[11px] font-bold uppercase tracking-[1px] text-muted">{d.format('ddd')}</div>
                  <div className={cx('mx-auto mt-0.5 grid h-8 w-8 place-items-center rounded-full font-semibold', today ? 'bg-primary text-white' : 'text-heading')}>
                    {d.format('D')}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Corps : lignes horaires + créneaux positionnés */}
          <div className="relative grid" style={{ gridTemplateColumns: '56px repeat(7, minmax(0,1fr))', gridAutoRows: '28px' }}>
            {hours.map((h, i) => (
              <div key={h} className="border-t border-slate-100 pr-2 text-right text-[11px] text-muted" style={{ gridColumn: 1, gridRow: `${i * 2 + 1} / span 2` }}>
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
            {/* Lignes de fond (séparateurs horaires) */}
            {hours.map((h, i) =>
              days.map((_, dcol) => (
                <div key={`bg-${h}-${dcol}`} className="border-l border-t border-slate-100" style={{ gridColumn: dcol + 2, gridRow: `${i * 2 + 1} / span 2` }} />
              )),
            )}

            {/* Leçons réservées */}
            {weekBooked.map((l) => {
              const pos = gridPosition(l.startsAt, l.endsAt);
              return (
                <div
                  key={l.id}
                  className="relative z-10 m-0.5 overflow-hidden rounded-xl bg-panel-indigo px-2 py-1 text-white shadow-card"
                  style={{ gridColumn: pos.col, gridRow: `${pos.row} / span ${pos.span}` }}
                  title={`Ma leçon · ${slotLabel(l.startsAt, l.endsAt)}`}
                >
                  <div className="truncate text-xs font-semibold">Ma leçon</div>
                  <div className="truncate text-[11px] text-white/80">
                    {dayjs(l.startsAt).format('HH:mm')} · {l.instructorName ?? '—'}
                  </div>
                </div>
              );
            })}

            {/* Créneaux disponibles */}
            {weekSlots.map((slot) => {
              const pos = gridPosition(slot.startsAt, slot.endsAt);
              return (
                <button
                  key={slot.id}
                  type="button"
                  onClick={() => openModal(slot)}
                  className="relative z-10 m-0.5 overflow-hidden rounded-xl border-2 border-dashed border-success/50 bg-success-light/70 px-2 py-1 text-left transition-colors hover:bg-success-light"
                  style={{ gridColumn: pos.col, gridRow: `${pos.row} / span ${pos.span}` }}
                  title={`Réserver — ${slotLabel(slot.startsAt, slot.endsAt)}`}
                >
                  <div className="pointer-events-none flex items-center gap-1">
                    <Plus size={12} className="shrink-0 text-success-dark" />
                    <span className="truncate text-xs font-semibold text-success-dark">Réserver</span>
                  </div>
                  <div className="pointer-events-none truncate text-[11px] text-success-dark/80">
                    {dayjs(slot.startsAt).format('HH:mm')} · {slot.instructorName ?? '—'}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Légende */}
      <div className="flex flex-wrap items-center gap-6 border-t border-line bg-surface-light px-6 py-3">
        <div className="flex items-center gap-2">
          <span className="h-3.5 w-3.5 rounded bg-panel-indigo" />
          <span className="text-xs text-secondary">Ma leçon</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-3.5 w-3.5 rounded border-2 border-dashed border-success/50 bg-success-light" />
          <span className="text-xs text-secondary">Créneau disponible</span>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="grid gap-5 sm:gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-title-md text-heading sm:text-title-lg">Réserver une leçon</h1>
          <p className="mt-1 text-sm text-secondary">
            Choisissez un créneau ouvert par votre auto-école — la réservation est confirmée instantanément.
          </p>
          {referent && (
            <p className="mt-3 inline-flex items-center gap-2 rounded-pill bg-surface-indigo px-3 py-1.5 text-[13px] text-primary-700">
              <UserCheck size={15} aria-hidden />
              Votre auto-école vous propose uniquement les créneaux de {referent}.
            </p>
          )}
        </div>
        {/* Liste / Calendrier : à partir de la tablette (le téléphone affiche la liste). */}
        <div className="hidden shrink-0 sm:block">
          <Segmented
            label="Affichage"
            value={view}
            onChange={setView}
            options={[
              { id: 'liste', label: 'Liste', icon: List },
              { id: 'calendrier', label: 'Calendrier', icon: CalendarDays },
            ]}
          />
        </div>
      </div>

      {/* Navigation de semaine */}
      <Card className="flex items-center justify-between gap-2 !px-3 !py-2.5 sm:!px-4">
        <Button variant="ghost" iconOnly icon={ChevronLeft} aria-label="Semaine précédente" onClick={() => setWeekOffset((w) => w - 1)} />
        <div className="min-w-0 text-center">
          <div className="text-sm font-semibold text-heading">
            Semaine du {weekStart.format('D')} au {weekStart.add(6, 'day').format('D MMMM YYYY')}
          </div>
          <div className="text-xs text-secondary">
            {weekSlots.length} créneau{weekSlots.length > 1 ? 'x' : ''} ouvert{weekSlots.length > 1 ? 's' : ''} à la réservation
            {weekOffset !== 0 && (
              <>
                {' · '}
                <button type="button" onClick={() => setWeekOffset(0)} className="font-medium text-primary-600 hover:underline">
                  Cette semaine
                </button>
              </>
            )}
          </div>
        </div>
        <Button variant="ghost" iconOnly icon={ChevronRight} aria-label="Semaine suivante" onClick={() => setWeekOffset((w) => w + 1)} />
      </Card>

      {actionError && (
        <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
          {actionError}
        </p>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-secondary">
          <Loader2 className="mr-2 animate-spin" size={18} /> Chargement des créneaux…
        </div>
      ) : view === 'liste' ? (
        list
      ) : (
        <>
          <div className="sm:hidden">{list}</div>
          <div className="hidden sm:block">{calendar}</div>
        </>
      )}

      {selected && (
        <ReserveModal
          slot={selected}
          schoolName={candidate?.school?.name ?? null}
          submitting={bookMutation.isPending}
          error={actionError}
          onConfirm={() => bookMutation.mutate(selected.id)}
          onClose={() => {
            setSelected(null);
            setActionError(null);
          }}
        />
      )}

      <ConfirmDialog
        open={!!toCancel}
        title="Annuler cette leçon ?"
        confirmLabel="Annuler la leçon"
        cancelLabel="Garder la leçon"
        tone="danger"
        pending={cancelMutation.isPending}
        onConfirm={() => toCancel && cancelMutation.mutate(toCancel.id)}
        onCancel={() => setToCancel(null)}
      >
        {toCancel && <>{slotLabel(toCancel.startsAt, toCancel.endsAt)}. Le créneau sera libéré pour un autre élève.</>}
      </ConfirmDialog>
    </div>
  );
}
